import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Input,
  Modal,
  Form,
  Tag,
  Space,
  Tooltip,
  Popconfirm,
  Typography,
  message,
  Row,
  Col,
  Card,
  Badge,
  Switch,
  Divider
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  ShopOutlined,
  PhoneOutlined,
  MailOutlined,
  UserOutlined
} from '@ant-design/icons';
import { proveedoresService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

function Proveedores() {
  const { user } = useAuth();
  const canEdit = user?.rol === 'root' || user?.rol === 'admin';
  const canDelete = user?.rol === 'root';
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProveedor, setEditingProveedor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [form] = Form.useForm();

  const fetchProveedores = useCallback(async (searchTerm = '') => {
    setLoading(true);
    try {
      const res = await proveedoresService.getAll({ search: searchTerm });
      setProveedores(res.data.data || []);
    } catch (error) {
      message.error(error.displayMessage || 'Error al cargar proveedores');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProveedores();
  }, [fetchProveedores]);

  const handleSearch = (value) => {
    setSearch(value);
    fetchProveedores(value);
  };

  const openCreateModal = () => {
    setEditingProveedor(null);
    form.resetFields();
    form.setFieldsValue({ activo: true });
    setModalOpen(true);
  };

  const openEditModal = (record) => {
    setEditingProveedor(record);
    form.setFieldsValue({
      nombre_comercial: record.nombre_comercial,
      codigo: record.codigo || '',
      razon_social: record.razon_social || '',
      rfc: record.rfc || '',
      telefono: record.telefono || '',
      email: record.email || '',
      direccion: record.direccion || '',
      contacto_nombre: record.contacto_nombre || '',
      contacto_telefono: record.contacto_telefono || '',
      contacto_email: record.contacto_email || '',
      notas: record.notas || '',
      activo: Boolean(record.activo)
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      if (editingProveedor) {
        await proveedoresService.update(editingProveedor.id, values);
        message.success('Proveedor actualizado exitosamente');
      } else {
        await proveedoresService.create(values);
        message.success('Proveedor creado exitosamente');
      }

      setModalOpen(false);
      fetchProveedores(search);
    } catch (error) {
      if (error.errorFields) return; // validation error, do nothing
      message.error(error.displayMessage || 'Error al guardar proveedor');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    try {
      await proveedoresService.delete(record.id);
      message.success(`Proveedor "${record.nombre_comercial}" eliminado exitosamente`);
      fetchProveedores(search);
    } catch (error) {
      message.error(error.displayMessage || 'Error al eliminar el proveedor');
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
      align: 'center',
      sorter: (a, b) => a.id - b.id,
      render: (id) => <Text type="secondary" style={{ fontSize: 12 }}>#{id}</Text>
    },
    {
      title: 'Proveedor',
      dataIndex: 'nombre_comercial',
      key: 'nombre_comercial',
      sorter: (a, b) => a.nombre_comercial.localeCompare(b.nombre_comercial),
      render: (nombre, record) => (
        <Space direction="vertical" size={0}>
          <Space size={6}>
            <ShopOutlined style={{ color: '#1677ff' }} />
            <Text strong>{nombre}</Text>
            {record.codigo && <Tag color="blue" style={{ fontSize: 11 }}>{record.codigo}</Tag>}
          </Space>
          {record.razon_social && (
            <Text type="secondary" style={{ fontSize: 12 }}>{record.razon_social}</Text>
          )}
        </Space>
      )
    },
    {
      title: 'RFC',
      dataIndex: 'rfc',
      key: 'rfc',
      width: 130,
      render: (rfc) => rfc
        ? <Text code style={{ fontSize: 12 }}>{rfc}</Text>
        : <Text type="secondary">—</Text>
    },
    {
      title: 'Contacto',
      key: 'contacto',
      width: 220,
      render: (_, record) => {
        const tieneContacto = record.telefono || record.email || record.contacto_nombre;
        if (!tieneContacto) return <Text type="secondary" style={{ fontStyle: 'italic' }}>Sin datos de contacto</Text>;
        return (
          <Space direction="vertical" size={0}>
            {record.contacto_nombre && (
              <Space size={4}>
                <UserOutlined style={{ fontSize: 11, color: '#8c8c8c' }} />
                <Text style={{ fontSize: 12 }}>{record.contacto_nombre}</Text>
              </Space>
            )}
            {record.telefono && (
              <Space size={4}>
                <PhoneOutlined style={{ fontSize: 11, color: '#8c8c8c' }} />
                <Text style={{ fontSize: 12 }}>{record.telefono}</Text>
              </Space>
            )}
            {record.email && (
              <Space size={4}>
                <MailOutlined style={{ fontSize: 11, color: '#8c8c8c' }} />
                <Text style={{ fontSize: 12 }}>{record.email}</Text>
              </Space>
            )}
          </Space>
        );
      }
    },
    {
      title: 'Productos',
      dataIndex: 'total_productos',
      key: 'total_productos',
      width: 110,
      align: 'center',
      sorter: (a, b) => (parseInt(a.total_productos) || 0) - (parseInt(b.total_productos) || 0),
      render: (val) => {
        const count = parseInt(val) || 0;
        return count > 0 ? (
          <Badge count={count} style={{ backgroundColor: '#1677ff' }} overflowCount={999} />
        ) : (
          <Tag color="default">0</Tag>
        );
      }
    },
    {
      title: 'Saldo Pendiente',
      dataIndex: 'saldo_pendiente',
      key: 'saldo_pendiente',
      width: 140,
      align: 'right',
      sorter: (a, b) => (parseFloat(a.saldo_pendiente) || 0) - (parseFloat(b.saldo_pendiente) || 0),
      render: (val) => {
        const monto = parseFloat(val) || 0;
        const formatted = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(monto);
        return monto > 0
          ? <Text strong style={{ color: '#d4380d' }}>{formatted}</Text>
          : <Text type="secondary">{formatted}</Text>;
      }
    },
    {
      title: 'Estado',
      dataIndex: 'activo',
      key: 'activo',
      width: 90,
      align: 'center',
      filters: [
        { text: 'Activo', value: 1 },
        { text: 'Inactivo', value: 0 }
      ],
      onFilter: (value, record) => Boolean(record.activo) === Boolean(value),
      render: (activo) =>
        activo ? <Tag color="success">Activo</Tag> : <Tag color="default">Inactivo</Tag>
    },
    {
      title: 'Fecha Alta',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      sorter: (a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
      render: (val) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {val ? dayjs(val).format('DD/MM/YYYY HH:mm') : '—'}
        </Text>
      )
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 110,
      fixed: 'right',
      render: (_, record) => {
        const totalProductos = parseInt(record.total_productos) || 0;
        const canDeleteRow = canDelete && totalProductos === 0;
        return (
          <Space size={4}>
            {canEdit && (
              <Tooltip title="Editar proveedor">
                <Button
                  type="primary"
                  ghost
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => openEditModal(record)}
                />
              </Tooltip>
            )}

            {canDelete && (
              <Tooltip
                title={
                  canDeleteRow
                    ? 'Eliminar proveedor'
                    : totalProductos > 0
                    ? 'No se puede eliminar, tiene productos asociados'
                    : 'Sin permisos para eliminar'
                }
              >
                <span>
                  <Popconfirm
                    title="¿Eliminar proveedor?"
                    description={`El proveedor "${record.nombre_comercial}" será eliminado permanentemente.`}
                    onConfirm={() => handleDelete(record)}
                    okText="Sí, eliminar"
                    cancelText="Cancelar"
                    icon={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
                    okButtonProps={{ danger: true }}
                    disabled={!canDeleteRow}
                  >
                    <Button
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      disabled={!canDeleteRow}
                    />
                  </Popconfirm>
                </span>
              </Tooltip>
            )}
          </Space>
        );
      }
    }
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Proveedores</Title>
          <Text type="secondary">Gestión de proveedores y sus datos de contacto</Text>
        </div>
      </div>

      {/* Filters & Actions */}
      <Card bordered={false} style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={12} md={10} lg={8}>
            <Input
              placeholder="Buscar por nombre, razón social, RFC o contacto..."
              prefix={<SearchOutlined />}
              allowClear
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                if (!e.target.value) fetchProveedores('');
              }}
              onPressEnter={(e) => handleSearch(e.target.value)}
              onClear={() => handleSearch('')}
            />
          </Col>
          <Col>
            <Button icon={<SearchOutlined />} onClick={() => handleSearch(search)}>
              Buscar
            </Button>
          </Col>
          <Col>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => { setSearch(''); fetchProveedores(''); }}
            >
              Limpiar
            </Button>
          </Col>
          {canEdit && (
            <Col flex="auto" style={{ textAlign: 'right' }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
                Nuevo Proveedor
              </Button>
            </Col>
          )}
        </Row>
      </Card>

      {/* Table */}
      <Card bordered={false}>
        <Table
          dataSource={proveedores}
          columns={columns}
          rowKey="id"
          loading={loading}
          size="small"
          scroll={{ x: 1000 }}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            pageSizeOptions: ['10', '15', '25', '50'],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} de ${total} proveedor${total !== 1 ? 'es' : ''}`,
            locale: { items_per_page: '/ página' }
          }}
          locale={{ emptyText: 'No se encontraron proveedores' }}
        />
      </Card>

      {/* Modal Create/Edit */}
      <Modal
        title={
          editingProveedor
            ? `Editar Proveedor: ${editingProveedor.nombre_comercial}`
            : 'Nuevo Proveedor'
        }
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        okText={editingProveedor ? 'Guardar Cambios' : 'Crear Proveedor'}
        cancelText="Cancelar"
        confirmLoading={saving}
        width={680}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          requiredMark="optional"
          initialValues={{ activo: true }}
          style={{ marginTop: 16 }}
        >
          <Divider orientation="left" plain style={{ marginTop: 0 }}>Información General</Divider>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="nombre_comercial"
                label="Nombre Comercial"
                rules={[
                  { required: true, message: 'El nombre comercial es requerido' },
                  { max: 150, message: 'Máximo 150 caracteres' },
                  { whitespace: true, message: 'El nombre no puede estar vacío' }
                ]}
              >
                <Input placeholder="Ej: Distribuidora El Águila" maxLength={150} showCount />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="razon_social"
                label="Razón Social"
                rules={[{ max: 200, message: 'Máximo 200 caracteres' }]}
              >
                <Input placeholder="Razón social (opcional)" maxLength={200} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={6}>
              <Form.Item
                name="codigo"
                label="Código"
                tooltip="Se usa para el folio de las órdenes de compra. Si se deja vacío, se genera automáticamente."
                rules={[{ max: 20, message: 'Máximo 20 caracteres' }]}
              >
                <Input placeholder="Auto" maxLength={20} style={{ textTransform: 'uppercase' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item name="rfc" label="RFC" rules={[{ max: 20, message: 'Máximo 20 caracteres' }]}>
                <Input placeholder="RFC (opcional)" maxLength={20} style={{ textTransform: 'uppercase' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item name="telefono" label="Teléfono" rules={[{ max: 30, message: 'Máximo 30 caracteres' }]}>
                <Input placeholder="Ej: 555-123-4567" maxLength={30} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item
                name="email"
                label="Correo Electrónico"
                rules={[
                  { type: 'email', message: 'Correo inválido' },
                  { max: 150, message: 'Máximo 150 caracteres' }
                ]}
              >
                <Input placeholder="contacto@proveedor.com" maxLength={150} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="direccion" label="Dirección">
            <TextArea placeholder="Dirección completa (opcional)" rows={2} maxLength={500} showCount />
          </Form.Item>

          <Divider orientation="left" plain>Datos de Contacto</Divider>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item
                name="contacto_nombre"
                label="Nombre del Contacto"
                rules={[{ max: 150, message: 'Máximo 150 caracteres' }]}
              >
                <Input placeholder="Persona de contacto" maxLength={150} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="contacto_telefono"
                label="Teléfono del Contacto"
                rules={[{ max: 30, message: 'Máximo 30 caracteres' }]}
              >
                <Input placeholder="Ej: 555-987-6543" maxLength={30} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="contacto_email"
                label="Correo del Contacto"
                rules={[
                  { type: 'email', message: 'Correo inválido' },
                  { max: 150, message: 'Máximo 150 caracteres' }
                ]}
              >
                <Input placeholder="contacto.persona@proveedor.com" maxLength={150} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="notas" label="Notas">
            <TextArea placeholder="Notas adicionales (opcional)" rows={2} maxLength={500} showCount />
          </Form.Item>

          <Form.Item name="activo" label="Estado" valuePropName="checked">
            <Switch checkedChildren="Activo" unCheckedChildren="Inactivo" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default Proveedores;
